/**
 * Schedule data layer — the single point of swap for the Calendar feature.
 *
 * TODAY (mock): zustand store seeded from `schedules.mock.ts`. All mutations are local.
 * TOMORROW (api): replace this file's internals with SWR.
 *   - read:  `useScheduleSource` becomes a thin wrapper around `useSWR('/schedules', fetcher)`.
 *   - write: each mutator becomes a `useSWRMutation` (or a plain `mutate(key, async () => ...)`)
 *            with optimistic update + revalidation via `mutate('/schedules')` on success.
 *            Engine routes already exist as stubs at `services/engine/src/api/routes/calendar.ts`.
 *
 * The PUBLIC API of this module (`useScheduleSource`, `useManagerSchedule`) must stay
 * stable so consumer components (WeekGrid, WindowEditorDialog, future Overview) don't change.
 *
 * Off-day convention: `ScheduleException.windows === null` means "off-day". When mapping
 * to the DB layer (`schedule_exceptions.is_off`), convert null → true and back.
 */

import { useMemo } from 'react'
import { create } from 'zustand'
import type {
  ManagerId,
  ManagerSchedule,
  ScheduleException,
  ScheduleWindow,
  Weekday,
} from '../types.ts'
import { buildMockSchedules } from './schedules.mock.ts'

interface ScheduleStore {
  schedules: ManagerSchedule[]
  ensureInit(reference: Date): void
  setRecurringWindow(args: {
    managerId: ManagerId
    weekday: Weekday
    /** null = append a new window for that weekday; number = update window at that index. */
    index: number | null
    window: ScheduleWindow
  }): void
  removeRecurringWindow(args: { managerId: ManagerId; weekday: Weekday; index: number }): void
  upsertException(args: {
    managerId: ManagerId
    date: string
    /** null = mark date as off-day. */
    windows: ScheduleWindow[] | null
  }): void
  removeException(args: { managerId: ManagerId; date: string; index: number | null }): void
}

const useScheduleStore = create<ScheduleStore>((set, get) => ({
  schedules: [],

  ensureInit(reference) {
    if (get().schedules.length > 0) return
    set({ schedules: buildMockSchedules(reference) })
  },

  // TODO(api): wrap in `useSWRMutation('/calendar/templates', fetcher)` → POST/PUT /api/calendar/templates/:managerId
  setRecurringWindow({ managerId, weekday, index, window }) {
    set((state) => ({
      schedules: state.schedules.map((s) => {
        if (s.managerId !== managerId) return s
        const days = [...s.weeklyTemplate]
        const dayIdx = days.findIndex((d) => d.weekday === weekday)
        if (dayIdx === -1) {
          days.push({ weekday, windows: [window] })
          return { ...s, weeklyTemplate: days }
        }
        const day = days[dayIdx]
        const windows = [...day.windows]
        if (index === null) {
          windows.push(window)
        } else {
          windows[index] = window
        }
        days[dayIdx] = { ...day, windows: sortWindows(windows) }
        return { ...s, weeklyTemplate: days }
      }),
    }))
  },

  // TODO(api): wrap in `useSWRMutation('/calendar/templates', fetcher)` → DELETE /api/calendar/templates/:managerId/:weekday/:index
  removeRecurringWindow({ managerId, weekday, index }) {
    set((state) => ({
      schedules: state.schedules.map((s) => {
        if (s.managerId !== managerId) return s
        const days = s.weeklyTemplate.flatMap((d) => {
          if (d.weekday !== weekday) return [d]
          const windows = d.windows.filter((_, i) => i !== index)
          return windows.length === 0 ? [] : [{ ...d, windows }]
        })
        return { ...s, weeklyTemplate: days }
      }),
    }))
  },

  // TODO(api): wrap in `useSWRMutation('/calendar/exceptions', fetcher)` → PUT /api/calendar/exceptions/:managerId/:date
  // (windows: null on the wire becomes is_off=true in DB; see schedules.ts schema)
  upsertException({ managerId, date, windows }) {
    set((state) => ({
      schedules: state.schedules.map((s) => {
        if (s.managerId !== managerId) return s
        const exceptions = [...s.exceptions]
        const idx = exceptions.findIndex((e) => e.date === date)
        const next: ScheduleException = { managerId, date, windows }
        if (idx === -1) exceptions.push(next)
        else exceptions[idx] = next
        return { ...s, exceptions }
      }),
    }))
  },

  // TODO(api): wrap in `useSWRMutation('/calendar/exceptions', fetcher)` → DELETE /api/calendar/exceptions/:managerId/:date
  removeException({ managerId, date, index }) {
    set((state) => ({
      schedules: state.schedules.map((s) => {
        if (s.managerId !== managerId) return s
        if (index !== null && index !== undefined) {
          // MVP: exceptions are one-per-date. The `index` param is reserved for a future
          // model where a single date can carry multiple exceptions; today we always
          // delete the whole exception by date.
        }
        const exceptions = s.exceptions.filter((e) => e.date !== date)
        return { ...s, exceptions }
      }),
    }))
  },
}))

function sortWindows(windows: ScheduleWindow[]): ScheduleWindow[] {
  return [...windows].sort((a, b) => a.startMinute - b.startMinute)
}

/**
 * Top-level data hook. Single point of swap when the API ships.
 *
 * TODO(api): replace the body of this hook with:
 *   const { data: schedules = [] } = useSWR('/schedules', fetcher)
 *   const setRecurringWindow = (args) =>
 *     mutate('/schedules', async () => ...optimistic..., { revalidate: true })
 *   ...same for the other three mutators (or `useSWRMutation` per write)
 *   return { schedules, setRecurringWindow, removeRecurringWindow, upsertException, removeException }
 *
 * The return shape stays identical, so consumers keep working untouched.
 */
export function useScheduleSource() {
  const initRef = useMemo(() => new Date(), [])
  useMemo(() => useScheduleStore.getState().ensureInit(initRef), [initRef])

  const schedules = useScheduleStore((s) => s.schedules)
  const setRecurringWindow = useScheduleStore((s) => s.setRecurringWindow)
  const removeRecurringWindow = useScheduleStore((s) => s.removeRecurringWindow)
  const upsertException = useScheduleStore((s) => s.upsertException)
  const removeException = useScheduleStore((s) => s.removeException)

  return {
    schedules,
    setRecurringWindow,
    removeRecurringWindow,
    upsertException,
    removeException,
  }
}

/**
 * Derived selector for a single manager's schedule.
 * TODO(api): becomes `useSWR(managerId ? \`/schedules/${managerId}\` : null, fetcher)`.
 */
export function useManagerSchedule(managerId: ManagerId | null): ManagerSchedule | null {
  const { schedules } = useScheduleSource()
  return useMemo(() => {
    if (!managerId) return null
    return schedules.find((s) => s.managerId === managerId) ?? null
  }, [schedules, managerId])
}
